/**
 * WebSitesList — Sitios web (estándar de listados del CMDB).
 *
 * Antes: tabla propia (`wol-table`) con el TLS pintado a mano en tres colores
 * fijos, borrado con `window.confirm` y la ficha abriendo **en edición**.
 *
 * Ahora: `ListPage` con buscador, filtros, columnas, vistas y export; la ficha
 * entra en **lectura** y el borrado usa el diálogo único.
 *
 * Botones propios:
 *  · **Abrir el sitio** — `https://<vhost>` en otra pestaña.
 *  · **Ver el servicio** y **Ver el backend** — a sus fichas en el Catálogo.
 *  · **Ver la máquina** — a la ficha del servidor o de la VM donde corre.
 *  · **Ver en el Diseñador** y **Copiar**.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  GlobeAltIcon,
  ListBulletIcon,
  Squares2X2Icon,
  WrenchScrewdriverIcon,
  LinkIcon,
  ServerStackIcon,
  ArrowTopRightOnSquareIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import WebSiteForm from './WebSiteForm';
import { useListView } from '../views/ViewsContext';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';
import useNavOrder from '../hooks/useNavOrder';

const STATUS_META = {
  online:      { label: 'Online',        badge: 'list-badge--on' },
  offline:     { label: 'Caído',         badge: 'list-badge--off' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
};

const TLS_META = {
  letsencrypt: { label: "Let's Encrypt", badge: 'list-badge--on' },
  manual:      { label: 'Manual',        badge: 'list-badge--warn' },
  none:        { label: 'Sin TLS',       badge: 'list-badge--off' },
};

const EMPTY_SITE = {
  service_id: '', vhost: '', root_path: '', backend_service_id: '',
  host_server_id: '', host_vm_id: '', status: 'online',
  tls_source: 'letsencrypt', tls_cert_ref: '', internal_ports: '', notes: '',
};

export default function WebSitesList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    if (!value || value === key) return fallback;
    return value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sites, setSites]       = useState([]);
  const [meta, setMeta]         = useState({ web_statuses: [] });
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');

  const [filterStatus, setFilterStatus]   = useState('');
  const [filterTls, setFilterTls]         = useState('');
  const [filterService, setFilterService] = useState('');
  const [filterBackend, setFilterBackend] = useState('');
  const [filterHostType, setFilterHostType] = useState('');
  const [filterNoTls, setFilterNoTls]     = useState('');
  const [filterBackendPresence, setFilterBackendPresence] = useState('');
  const [groupMode, setGroupMode]         = useState('');

  const [selected, setSelected]       = useState(null);
  const [initialMode, setInitialMode] = useState('edit');
  const [hlId, setHlId]               = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [metaResp, list, svcs] = await Promise.all([
        api.getAppsMeta(companyId),
        api.getWebSites(companyId),
        api.getServices(companyId),
      ]);
      setMeta(metaResp || { web_statuses: [] });
      setSites(Array.isArray(list) ? list : []);
      setServices(Array.isArray(svcs) ? svcs : (svcs.services || []));
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los sitios web');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !sites.length) return undefined;
    const id = Number(raw);
    const target = sites.find(s => s.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { setInitialMode('view'); setSelected(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`web-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
  }, [searchParams, sites, setSearchParams]);

  /* ── ficha ──────────────────────────────────────────────────────────────── */
  const openView   = (s) => { setInitialMode('view');   setSelected(s); };
  const openEdit   = (s) => { setInitialMode('edit');   setSelected(s); };
  const openCreate = ()  => { setInitialMode('create'); setSelected({ ...EMPTY_SITE }); };
  const closeForm  = ()  => { setSelected(null); load(); };

  const handleDelete = async (s) => {
    try {
      await api.deleteWebSite(s.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  /* ── utilidades ─────────────────────────────────────────────────────────── */
  const serviceLabel = useCallback(
    (id) => services.find(s => s.id === id)?.display_name
      || services.find(s => s.id === id)?.name || id,
    [services],
  );

  const hostLabel = (s) => s.host_vm_name || s.host_server_name || null;
  const hostPath = (s) => {
    if (s.host_vm_id) return `/virt/vms?destacar=${s.host_vm_id}&abrir=1`;
    if (s.host_server_id) return `/infrastructure/servers?destacar=${s.host_server_id}&abrir=1`;
    return null;
  };

  const serviceOptions = useMemo(() => {
    const seen = new Map();
    sites.forEach(s => { if (s.service_id) seen.set(String(s.service_id), s.service_name || serviceLabel(s.service_id)); });
    return [...seen.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [sites, serviceLabel]);

  const backendOptions = useMemo(() => {
    const seen = new Map();
    sites.forEach(s => { if (s.backend_service_id) seen.set(String(s.backend_service_id), s.backend_service_name || serviceLabel(s.backend_service_id)); });
    return [...seen.entries()]
      .sort((a, b) => String(a[1]).localeCompare(String(b[1]), 'es'))
      .map(([value, label]) => ({ value, label }));
  }, [sites, serviceLabel]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return sites.filter((s) => {
      if (filterStatus && (s.status || 'online') !== filterStatus) return false;
      if (filterTls && (s.tls_source || 'none') !== filterTls) return false;
      if (filterService && String(s.service_id || '') !== String(filterService)) return false;
      if (filterBackend && String(s.backend_service_id || '') !== String(filterBackend)) return false;
      if (filterHostType === 'vm' && !s.host_vm_id) return false;
      if (filterHostType === 'server' && !s.host_server_id) return false;
      if (filterNoTls === 'yes' && (s.tls_source || 'none') !== 'none' && s.tls_cert_ref) return false;
      if (filterNoTls === 'no' && ((s.tls_source || 'none') === 'none' && !s.tls_cert_ref)) return false;
      if (filterBackendPresence === 'with' && !s.backend_service_id) return false;
      if (filterBackendPresence === 'without' && s.backend_service_id) return false;
      if (!term) return true;
      return [
        s.vhost, s.root_path, s.notes, s.tls_cert_ref,
        s.service_name, serviceLabel(s.service_id),
        s.backend_service_name, hostLabel(s),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [sites, search, filterStatus, filterTls, filterService, filterBackend,
    filterHostType, filterNoTls, filterBackendPresence, serviceLabel]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navSites = useNavOrder(filtered, selected, selected !== null);

  /* ── columnas ───────────────────────────────────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'vhost',
        label: 'VHost',
        sortable: true,
        render: (s) => (
          <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <span style={{ fontWeight: 600 }}>{s.vhost}</span>
            {s.root_path && <span className="list-hint list-code">{s.root_path}</span>}
          </span>
        ),
      },
      {
        key: 'service',
        label: tr('web.service', 'Servicio'),
        sortable: true,
        sortValue: (s) => s.service_name || '',
        render: (s) => (s.service_id
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/services?destacar=${s.service_id}&abrir=1`);
              }}
              title={tr('web.view_service', 'Ver el servicio')}
            >
              {s.service_name || serviceLabel(s.service_id)}
            </button>
          )
          : dash),
      },
      {
        key: 'backend',
        label: tr('web.backend', 'Backend'),
        sortable: true,
        sortValue: (s) => s.backend_service_name || '',
        render: (s) => (s.backend_service_id
          ? (
            <button
              type="button"
              className="list-badge"
              style={{ cursor: 'pointer' }}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/services?destacar=${s.backend_service_id}&abrir=1`);
              }}
              title={tr('web.view_backend', 'Ver el backend')}
            >
              {s.backend_service_name || serviceLabel(s.backend_service_id)}
            </button>
          )
          : dash),
      },
      {
        key: 'host',
        label: tr('web.host', 'Máquina'),
        sortable: true,
        sortValue: (s) => hostLabel(s) || '',
        render: (s) => {
          const label = hostLabel(s);
          if (!label) return dash;
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
              <span>{label}</span>
              <span className="list-hint">
                {s.host_vm_id ? tr('dbinst.host_vm', 'Máquina virtual') : tr('dbinst.host_server', 'Servidor físico')}
              </span>
            </span>
          );
        },
      },
      {
        key: 'status',
        label: tr('ipam.status', 'Estado'),
        sortable: true,
        sortValue: (s) => s.status || 'online',
        render: (s) => {
          const meta_ = STATUS_META[s.status] || { label: s.status || '—', badge: '' };
          return <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>;
        },
      },
      {
        key: 'tls',
        label: 'TLS',
        sortable: true,
        sortValue: (s) => s.tls_source || '',
        render: (s) => {
          const meta_ = TLS_META[s.tls_source] || (s.tls_cert_ref
            ? { label: s.tls_cert_ref, badge: 'list-badge--warn' }
            : TLS_META.none);
          return (
            <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
              <span className={`list-badge ${meta_.badge}`}>{meta_.label}</span>
              {s.tls_cert_ref && s.tls_source && s.tls_source !== 'none' && (
                <span className="list-hint list-code">{s.tls_cert_ref}</span>
              )}
            </span>
          );
        },
      },
      {
        key: 'internal_ports',
        label: tr('web.internal_ports', 'Puertos internos'),
        render: (s) => (s.internal_ports ? <span className="list-code">{s.internal_ports}</span> : dash),
      },
      {
        key: 'notes',
        label: tr('description', 'Notas'),
        render: (s) => s.notes || dash,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate, serviceLabel]);

  const defaultVisible = ['vhost', 'service', 'backend', 'host', 'status', 'tls'];

  const filters = [
    {
      key: 'status', label: tr('ipam.status', 'Estado'), allLabel: tr('ipam.status_all', 'Todos los estados'),
      value: filterStatus, onChange: setFilterStatus,
      options: (meta.web_statuses || Object.keys(STATUS_META)).map(s => ({
        value: s, label: (STATUS_META[s] || {}).label || s,
      })),
    },
    {
      key: 'tls', label: 'TLS', allLabel: tr('web.tls_all', 'Cualquier origen'),
      value: filterTls, onChange: setFilterTls,
      options: Object.entries(TLS_META).map(([value, meta_]) => ({ value, label: meta_.label })),
    },
    {
      key: 'service', label: tr('web.service', 'Servicio'), allLabel: tr('dbinst.service_all', 'Cualquier servicio'),
      value: filterService, onChange: setFilterService, options: serviceOptions,
    },
    {
      key: 'backend', label: tr('web.backend', 'Backend'), allLabel: tr('web.backend_all', 'Cualquier backend'),
      value: filterBackend, onChange: setFilterBackend, options: backendOptions,
    },
    {
      key: 'hostType', label: tr('web.host_type', 'Tipo de máquina'), allLabel: tr('dbinst.host_all', 'Cualquier máquina'),
      value: filterHostType, onChange: setFilterHostType,
      options: [
        { value: 'server', label: tr('dbinst.host_server', 'Servidor físico') },
        { value: 'vm', label: tr('dbinst.host_vm', 'Máquina virtual') },
      ],
    },
    {
      key: 'notls', label: tr('web.without_tls', 'Sin TLS'), allLabel: tr('web.notls_all', 'Con o sin TLS'),
      value: filterNoTls, onChange: setFilterNoTls,
      options: [
        { value: 'yes', label: tr('web.only_without_tls', 'Solo los que no tienen TLS') },
        { value: 'no', label: tr('web.only_with_tls', 'Solo los que tienen TLS') },
      ],
    },
    {
      key: 'backendPresence', label: tr('web.backend', 'Backend'), allLabel: tr('web.backend_presence_all', 'Con o sin backend'),
      value: filterBackendPresence, onChange: setFilterBackendPresence,
      options: [
        { value: 'with', label: tr('web.backend_with', 'Con backend') },
        { value: 'without', label: tr('web.backend_without', 'Sin backend') },
      ],
    },
    {
      key: 'group', label: tr('web.group', 'Agrupar por'), allLabel: tr('web.group_none', 'Sin agrupar'),
      value: groupMode, onChange: setGroupMode,
      options: [
        { value: 'service', label: tr('web.service', 'Servicio') },
        { value: 'host', label: tr('web.host', 'Máquina') },
      ],
    },
  ];

  const groupBy = useMemo(() => {
    if (groupMode === 'service') return (s) => s.service_name || serviceLabel(s.service_id) || tr('dbinst.no_service', 'Sin servicio');
    if (groupMode === 'host') return (s) => hostLabel(s) || tr('dbinst.no_host', 'Sin host');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, serviceLabel]);

  const subtitle = `${sites.length} ${tr('web.plural', 'sitios')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtrados')}`;

  const exportRows = useMemo(() => filtered.map(s => ({
    'VHost': s.vhost || '',
    'Ruta raíz': s.root_path || '',
    'Servicio': s.service_name || serviceLabel(s.service_id),
    'Backend': s.backend_service_name || (s.backend_service_id ? serviceLabel(s.backend_service_id) : ''),
    'Máquina': hostLabel(s) || '',
    'Tipo de máquina': s.host_vm_id ? 'VM' : s.host_server_id ? 'Servidor' : '',
    'Estado': (STATUS_META[s.status] || {}).label || s.status || '',
    'Certificado TLS': (TLS_META[s.tls_source] || {}).label || '',
    'Referencia del certificado': s.tls_cert_ref || '',
    'Puertos internos': s.internal_ports || '',
    'Notas': s.notes || '',
  })), [filtered, serviceLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Tarjetas: lo esencial del sitio, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((s) => {
          const st = STATUS_META[s.status] || { label: s.status || '—', badge: '' };
          const tls = TLS_META[s.tls_source] || TLS_META.none;
          return (
            <div
              key={s.id}
              id={`web-card-${s.id}`}
              className={`list-card${s.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(s)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(s); }}
              title={`${tr('action.view', 'Ver')} ${s.vhost}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">{s.vhost}</div>
                  {s.service_name && <span className="list-hint">{s.service_name}</span>}
                </div>
                <span className={`list-badge ${st.badge}`}>{st.label}</span>
              </div>

              <div className="list-card-meta">
                <span className={`list-badge ${tls.badge}`}>{tls.label}</span>
                {s.backend_service_name && <span>→ {s.backend_service_name}</span>}
                {hostLabel(s) && <span>{hostLabel(s)}</span>}
              </div>

              <div className="list-card-actions">{rowActions(s)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const WEB_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada sitio'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('web_sites', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">
          {tr('web.select_company', 'Selecciona una empresa para ver los sitios web.')}
        </div>
      </div>
    );
  }

  return (
    <>
      {selected !== null && (
        <WebSiteForm
          site={selected && selected.id ? selected : null}
          companyId={companyId}
          meta={meta}
          services={services}
          initialMode={initialMode}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
          navItems={navSites}
          navIndex={selected && selected.id ? navSites.findIndex(s => String(s.id) === String(selected.id)) : -1}
          onNavigate={(destino) => setSelected(destino)}
          onClose={closeForm}
          onSaved={(created) => { load(); if (created?.id) setSelected(created); }}
        />
      )}

      <div style={{ display: selected !== null ? 'none' : undefined }}>
        <ListPage
          icon={GlobeAltIcon}
          title={tr('web.title', 'Sitios web')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('web.new', 'Nuevo sitio')}
          createTitle={tr('web.new_hint', 'VHost servido por un servicio, con su backend y su certificado')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('web.search', 'Buscar por vhost, servicio, backend, máquina…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="sitios-web"
          exportSheetName="Sitios web"

          columns={columns}
          columnStorageKey="cmdb_cols_web_sites"
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(s) => s.id}
          rowId={(s) => `web-row-${s.id}`}
          rowClassName={(s) => (s.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(s) => `${tr('action.view', 'Ver')} ${s.vhost}`}
          groupBy={groupBy}

          views={WEB_VIEWS}
          listKey="web_sites"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando sitios web…')}
          error={error}

          empty={{
            icon: '🌐',
            title: sites.length === 0
              ? tr('web.empty', 'No hay sitios')
              : tr('msg.no_results', 'Ningún sitio coincide con los filtros'),
            subtitle: sites.length === 0
              ? tr('web.empty_hint', 'Registra tus vhosts para relacionarlos con sus servicios y certificados.')
              : undefined,
            action: sites.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('web.new', 'Nuevo sitio')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (s) => (
              <>
                {/* Abrir el sitio */}
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => window.open(`https://${s.vhost}`, '_blank', 'noopener')}
                  title={tr('web.open', 'Abrir el sitio en otra pestaña')}
                >
                  <ArrowTopRightOnSquareIcon />
                </button>
                {/* Ver el servicio */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!s.service_id}
                  onClick={() => navigate(`/services?destacar=${s.service_id}&abrir=1`)}
                  title={tr('web.view_service', 'Ver el servicio')}
                >
                  <WrenchScrewdriverIcon />
                </button>
                {/* Ver el backend */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!s.backend_service_id}
                  onClick={() => navigate(`/services?destacar=${s.backend_service_id}&abrir=1`)}
                  title={tr('web.view_backend', 'Ver el backend')}
                >
                  <LinkIcon />
                </button>
                {/* Ver la máquina */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!hostPath(s)}
                  onClick={() => { const p = hostPath(s); if (p) navigate(p); }}
                  title={hostLabel(s)
                    ? `${tr('web.view_host', 'Ver la máquina donde corre')}: ${hostLabel(s)}`
                    : tr('dbinst.no_host', 'Sin host')}
                >
                  <ServerStackIcon />
                </button>
                {/* Ver en el Diseñador */}
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!s.service_id}
                  onClick={() => navigate(`/services/designer?service=${s.service_id}`)}
                  title={tr('virt.view_in_designer', 'Ver en el Diseñador de servicios')}
                >
                  <ShareIcon />
                </button>
              </>
            ),
            onCopy: undefined,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('web.delete_title', 'Eliminar sitio'),
            deleteMessage: (s) => (
              <>
                {tr('virt.delete_confirm', '¿Seguro que quieres eliminar')} <strong>{s.vhost}</strong>?{' '}
                {tr('web.delete_used', 'No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>
    </>
  );
}
